import { DateTimeStr } from '@mi-front/core';
import { LocaleRelativeTime } from '@mi-front/ui';
import { Box, Typography } from '@mui/material';
import { useIntl } from 'react-intl';
import { formatTimezoneDisplayName } from './format-timezone-display-name';
import { LDate } from './LDate';
import { LTime } from './LTime';

const ONE_MINUTE_MS = 60 * 1000;

interface Props {
  value: DateTimeStr | string;
  showRelative?: boolean | 'inline';
  showSeconds?: boolean;
  /**
   * IANA timezone id for the date and time segments (passed to `LDate` / `LTime`).
   * When omitted, those children fall back to timezone context or the browser default.
   */
  tz?: string;
  /**
   * When true, appends a short timezone label (e.g. `(ART)`) if `tz` is set and differs from the
   * browser timezone; otherwise nothing extra is rendered.
   */
  showTz?: boolean;
}

/** Returns true when the given date is less than one minute in the past (not future). */
function isLessThanOneMinuteAgo(date: Date): boolean {
  const diffMs = Date.now() - date.getTime();
  return diffMs >= 0 && diffMs < ONE_MINUTE_MS;
}

/** Renders a locale-aware date + time; optional `tz` scopes both parts without a `TimezoneCtxProvider`. */
export const LDateTime = (p: Props) => {
  const { value, showSeconds = false, tz, showTz = false } = p;
  const { formatMessage: $t, locale } = useIntl();
  const date = new Date(value);
  const timezoneLabel = showTz ? formatTimezoneDisplayName(tz, locale) : null;

  if (isLessThanOneMinuteAgo(date)) {
    return (
      <Box display="inline-flex" flexDirection="column">
        {$t({
          id: 'date.justNow',
          defaultMessage: 'Hace instantes',
        })}
      </Box>
    );
  }

  return (
    <Box display="inline-flex" flexDirection="column">
      <Box display="flex" gap={0.5}>
        <LDate value={value as DateTimeStr} day="2-digit" month="2-digit" year="2-digit" tz={tz} />
        <LTime value={value as DateTimeStr} showSeconds={showSeconds} tz={tz} />

        {timezoneLabel != null && (
          <Typography component="span" sx={{ typography: 'caption', color: 'text.disabled' }}>
            ({timezoneLabel})
          </Typography>
        )}

        {p.showRelative && p.showRelative === 'inline' && (
          <>
            (<LocaleRelativeTime value={value} />)
          </>
        )}
      </Box>

      {p.showRelative && p.showRelative !== 'inline' && (
        <Typography color="text.disabled" fontSize="0.7rem">
          <LocaleRelativeTime value={value} />
        </Typography>
      )}
    </Box>
  );
};
